export const NONRESPONSIVE_IMAGES = /^.*\.(gif)/;

export const ANIMATED_GIF = /-999x-999/;
export const DEFAULT_WIDTHS = [
    220, 480, 640, 720, 960, 1020, 1200, 1280, 1400, 1800, 2000,
];

export function isString(
    param: string | string[] | undefined
): param is string {
    if (!param || typeof param !== "string") {
        return false;
    }

    return true;
}

export function makeSrcset(
    baseUrl: string,
    nativeWidth: number,
    nativeHeight = -1
) {
    if (!baseUrl || NONRESPONSIVE_IMAGES.test(baseUrl)) {
        return undefined;
    }

    const widths = DEFAULT_WIDTHS.map((width) => Math.min(width, nativeWidth));

    // Use a set to remove duplicate widths
    return Array.from(new Set(widths))
        .map((width) => {
            const calculatedHeight =
                nativeHeight > 0
                    ? Math.round(nativeHeight * (width / nativeWidth))
                    : -1;

            return makeImageResizeUrl(baseUrl, width, calculatedHeight, true);
        })
        .join(", ");
}

export function getHeightFromWidthAndAspectRatio(
    width: number,
    aspectRatio?: number
) {
    if (!aspectRatio) {
        return -1;
    }

    return Math.round(width / aspectRatio);
}

type MakeRetinaSrcSetParams = {
    mobileMaxWidth: number;
    desktopMaxWidth: number;
    aspectRatio?: number;
    baseUrl: string;
};

export function makeRetinaSrcsets({
    mobileMaxWidth,
    desktopMaxWidth,
    aspectRatio,
    baseUrl,
}: MakeRetinaSrcSetParams) {
    if (NONRESPONSIVE_IMAGES.test(baseUrl)) return undefined;

    return {
        mobile: [1, 2]
            .map((pixelDensity) => {
                const w = mobileMaxWidth * pixelDensity;
                const h = getHeightFromWidthAndAspectRatio(w, aspectRatio);

                return makeImageResizeUrl(baseUrl, w, h, true, pixelDensity);
            })
            .join(", "),
        desktop: [1, 2]
            .map((pixelDensity) => {
                const w = desktopMaxWidth * pixelDensity;
                const h = getHeightFromWidthAndAspectRatio(w, aspectRatio);

                return makeImageResizeUrl(baseUrl, w, h, true, pixelDensity);
            })
            .join(", "),
    };
}

export function makeImageResizeUrl(
    baseUrl: string,
    width = 25,
    height = 19,
    srcSet?: boolean,
    srcSetPixelDensity?: number
) {
    if (ANIMATED_GIF.test(baseUrl)) {
        return baseUrl
            .replace(/^http:\/\//, "//")
            .replace(/^\/\//, "https://")
            .replace(/.webp/, ".gif");
    }

    return baseUrl
        .replace(/^http:\/\//, "//")
        .replace(/^\/\//, "https://")
        .replace(
            /(\/)(-*\d+x-*\d+)(.\w+)$/,
            `$1${width}x${height}$3${
                srcSet
                    ? ` ${
                          srcSetPixelDensity
                              ? `${srcSetPixelDensity}x`
                              : `${width}w`
                      }`
                    : ""
            }`
        );
}

export function makeFallbackImage(
    baseUrl: string,
    width = 25,
    height = 19,
    srcSet?: boolean,
    srcSetPixelDensity?: number
) {
    return makeImageResizeUrl(
        baseUrl,
        width,
        height,
        srcSet,
        srcSetPixelDensity
    )?.replace(/.webp/, ".jpg");
}
